import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface UserLocationWeather {
  temp: number;
  feels_like?: number;
  humidity: number;
  description: string;
  icon: string;
  wind_speed: number;
  wind_gust?: number;
  city: string;
}

const CACHE_KEY = "user-loc-weather-v1";
const CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface CachedEntry {
  fetchedAt: number;
  key: string;
  weather: UserLocationWeather;
}

function readCache(): CachedEntry | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedEntry;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(entry: CachedEntry) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    /* ignore quota */
  }
}

function getPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) {
      reject(new Error("Geolocation not supported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      maximumAge: 10 * 60 * 1000,
      timeout: 10_000,
    });
  });
}

export function useUserLocationWeather() {
  const [weather, setWeather] = useState<UserLocationWeather | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [refreshIndex, setRefreshIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      // Try cache first to avoid re-prompting on every nav (skip on manual refresh)
      const cached = refreshIndex === 0 ? readCache() : null;
      if (cached) {
        setWeather(cached.weather);
        setFetchedAt(cached.fetchedAt);
        setLoading(false);
        return;
      }

      try {
        const pos = await getPosition();
        if (cancelled) return;
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        const key = `${lat.toFixed(2)},${lon.toFixed(2)}`;

        const { data, error } = await supabase.functions.invoke("get-job-weather", {
          body: { coords: { lat, lon }, force: refreshIndex > 0 },
        });
        if (cancelled) return;
        if (error) throw error;
        const result = data?._geo;
        if (result?.current && !result.error) {
          setWeather(result.current);
          const now = Date.now();
          setFetchedAt(now);
          writeCache({ fetchedAt: now, key, weather: result.current });
        } else {
          setWeather(null);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const code = (err as GeolocationPositionError | undefined)?.code;
        if (code === 1) setDenied(true);
        setWeather(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [refreshIndex]);

  const refresh = () => {
    try {
      sessionStorage.removeItem(CACHE_KEY);
    } catch {
      /* ignore */
    }
    setRefreshIndex((i) => i + 1);
  };

  return { weather, fetchedAt, loading, denied, refresh };
}